import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion, CompanionAbility } from '../store/gameStore';

interface BattleCompanion extends Companion {
  currentHp: number;
  maxHp: number;
  attack: number;
  defense: number;
  battleLevel: number;
  battleExperience: number;
}

interface BattleResult {
  winner: 'player' | 'enemy';
  playerTeam: BattleCompanion[];
  enemyTeam: BattleCompanion[];
  rewards: {
    coins: number;
    gems: number;
    experience: number;
  };
  capturedCompanion?: Companion | null;
}

const CompanionArena: React.FC = () => {
  const { companions, coins, addCoins, addGems, addExperience, formatNumber, updateCompanionBattleStats, updateCompanionNormalStats, addCompanionAbility, addCompanionToCollection, addToTraining, removeFromTraining, getTrainingCost, canAffordTraining, getMaxTrainingCapacity, getArenaLevel, setArenaLevel, getArenaDifficultyMultiplier, getArenaResetCount, canResetArena, resetArena, getCompanionArmor, getCompanionWeapons, reduceArmorDurability, reduceWeaponDurability, getGlobalBonuses } = useGameStore();
  const [selectedTeam, setSelectedTeam] = useState<Companion[]>([]);
  
  const [currentBattle, setCurrentBattle] = useState<{
    playerTeam: BattleCompanion[];
    enemyTeam: BattleCompanion[];
    turn: number;
    phase: 'preparation' | 'battle' | 'result';
  } | null>(null);
  const [battleResult, setBattleResult] = useState<BattleResult | null>(null);
  const [battleLog, setBattleLog] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'arena' | 'barracks'>('arena');
  const [autoBattle, setAutoBattle] = useState(false);
  const [autoBattleSpeed, setAutoBattleSpeed] = useState(1); // 1 = normal, 2 = x2 speed
  const [lastUsedTeam, setLastUsedTeam] = useState<Companion[]>([]);
  const [battleView, setBattleView] = useState<'participants' | 'log'>('participants');
  const [showBattleAnalysis, setShowBattleAnalysis] = useState(false);
  const [speedBoostEndTime, setSpeedBoostEndTime] = useState<number | null>(null);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const trainingCompanions = companions.training;
  const arenaLevel = getArenaLevel();

  // Verbleibende Zeit für Speed Boost berechnen
  const getSpeedBoostTimeRemaining = () => {
    if (!speedBoostEndTime) return 0;
    const remaining = Math.max(0, speedBoostEndTime - Date.now());
    return Math.ceil(remaining / 1000); // In Sekunden
  };

  // Formatierte Zeit-Anzeige für "Zeit bis leer"
  const formatTimeUntilEmpty = () => {
    if (!canAffordTraining() || trainingCompanions.length === 0) return 'N/A';
    
    const secondsUntilEmpty = Math.floor(coins / getTrainingCost());
    
    if (secondsUntilEmpty < 60) {
      return `${secondsUntilEmpty}s`;
    } else if (secondsUntilEmpty < 3600) {
      const minutes = Math.floor(secondsUntilEmpty / 60);
      const seconds = secondsUntilEmpty % 60;
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    } else if (secondsUntilEmpty < 86400) {
      const hours = Math.floor(secondsUntilEmpty / 3600);
      const minutes = Math.floor((secondsUntilEmpty % 3600) / 60);
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${hours}h`;
      }
    } else if (secondsUntilEmpty < 31536000) {
      const days = Math.floor(secondsUntilEmpty / 86400);
      const hours = Math.floor((secondsUntilEmpty % 86400) / 3600);
      if (hours > 0) {
        return `${days}d ${hours}h`;
      } else {
        return `${days}d`;
      }
    } else if (secondsUntilEmpty < 31536000000) { // 1000 Jahre
      const years = Math.floor(secondsUntilEmpty / 31536000);
      const days = Math.floor((secondsUntilEmpty % 31536000) / 86400);
      if (days > 0) {
        return `${years}y ${days}d`;
      } else {
        return `${years}y`;
      }
    } else {
      // Lichtjahre für extrem hohe Level! 🌌
      const lightYears = (secondsUntilEmpty / 31536000 / 9.461e12).toFixed(7);
      return `${lightYears}ly 🌌`;
    }
  };

  // Debug-Ausgabe für Begleiter-Level beim Laden der Arena
  React.useEffect(() => {
    console.log('🏛️ Arena geladen - Begleiter-Level:');
    companions.discovered.forEach(companion => {
      if (companion.battleLevel && companion.battleLevel > 1) {
        console.log(`  ${companion.name}: Battle Level ${companion.battleLevel}, Normal Level ${companion.level || 1}`);
      }
    });
  }, [companions.discovered]);

  // Timer für Speed Boost Anzeige
  React.useEffect(() => {
    if (speedBoostEndTime) {
      const interval = setInterval(() => {
        const remaining = getSpeedBoostTimeRemaining();
        if (remaining <= 0) {
          setSpeedBoostEndTime(null);
          setAutoBattleSpeed(1);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [speedBoostEndTime, getSpeedBoostTimeRemaining]);

  // Konvertiere Companion zu BattleCompanion
  const toBattleCompanion = (companion: Companion, isWild: boolean = false): BattleCompanion => {
    const baseLevel = companion.battleLevel || 1;
    const difficultyMultiplier = getArenaDifficultyMultiplier();
    const levelMultiplier = isWild ? Math.max(0.3, arenaLevel * 0.4 * difficultyMultiplier) : 1;
    const finalLevel = Math.floor(baseLevel * levelMultiplier);
    
    // Forschungs-Boni anwenden (nur für Spieler-Begleiter, nicht für wilde Gegner)
    const globalBonuses = getGlobalBonuses();
    const researchAttackBonus = isWild ? 0 : globalBonuses.attack;
    const researchDefenseBonus = isWild ? 0 : globalBonuses.defense;
    const researchHpBonus = isWild ? 0 : globalBonuses.hp;
    
          // Debug-Ausgabe für gelevelte Begleiter
      if (!isWild && baseLevel > 1) {
        console.log(`🎯 ${companion.name}: Base Level ${baseLevel}, Final Level ${finalLevel}`);
      }
      
      // Debug-Ausgabe für mystische und göttliche Begleiter
      const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
      const isGodlyLegendary = companion.name.startsWith('★') && companion.name.endsWith('★');
      
      if (isMysticDivine || isGodlyLegendary) {
        console.log(`🌟 ${companion.name}: ${isMysticDivine ? 'Mystisch' : 'Göttlich'} Begleiter erkannt`);
      }
    
    if (isWild) {
      // Sehr schwache Skalierung für wilde Gegner
      const levelBonus = Math.floor(finalLevel * 0.8); // Nur lineare Skalierung, keine quadratische
      
      const baseHp = 50 + (levelBonus * 6); // Viel weniger HP
      const baseAttack = 5 + (levelBonus * 1.5) + (companion.abilities.find(a => a.type === 'clickMultiplier')?.value || 0) * 0.2; // Minimaler Angriff
      const baseDefense = 3 + (levelBonus * 1) + (companion.abilities.find(a => a.type === 'gardenBonus')?.value || 0) * 0.1; // Minimale Verteidigung

      return {
        ...companion,
        currentHp: baseHp,
        maxHp: baseHp,
        attack: Math.floor(baseAttack),
        defense: Math.floor(baseDefense),
        battleLevel: finalLevel,
        battleExperience: companion.battleExperience || 0,
      };
    } else {
      // Normale Skalierung für Spieler-Begleiter
      const levelBonus = finalLevel + Math.floor(finalLevel * finalLevel * 0.1);
      
      // Mystisch göttlich legendäre Begleiter haben extrem hohe HP
      const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
      const isGodlyLegendary = companion.name.startsWith('★') && companion.name.endsWith('★');
      
      let baseHp, baseAttack, baseDefense;
      
      if (isMysticDivine) {
        // Mystisch göttlich: Extrem hohe Werte
        baseHp = 5000 + (levelBonus * 500);
        baseAttack = 500 + (levelBonus * 100) + (companion.abilities.find(a => a.type === 'clickMultiplier')?.value || 0) * 10;
        baseDefense = 300 + (levelBonus * 80) + (companion.abilities.find(a => a.type === 'gardenBonus')?.value || 0) * 8;
      } else if (isGodlyLegendary) {
        // Göttlich legendär: Sehr hohe Werte
        baseHp = 2000 + (levelBonus * 200);
        baseAttack = 200 + (levelBonus * 50) + (companion.abilities.find(a => a.type === 'clickMultiplier')?.value || 0) * 5;
        baseDefense = 150 + (levelBonus * 40) + (companion.abilities.find(a => a.type === 'gardenBonus')?.value || 0) * 4;
      } else {
        // Normale Begleiter
        baseHp = 80 + (levelBonus * 15);
        baseAttack = 15 + (levelBonus * 8) + (companion.abilities.find(a => a.type === 'clickMultiplier')?.value || 0) * 1.2;
        baseDefense = 10 + (levelBonus * 6) + (companion.abilities.find(a => a.type === 'gardenBonus')?.value || 0) * 0.8;
      }

      // Ausrüstung-Boni hinzufügen
      let equipmentAttackBonus = 0;
      let equipmentDefenseBonus = 0;
      
      // Waffen-Boni
      const companionWeapons = getCompanionWeapons(companion.id);
      companionWeapons.forEach(weapon => {
        if (weapon.durability > 0) {
          equipmentAttackBonus += weapon.attack;
        }
      });
      
      // Rüstungs-Boni
      const companionArmor = getCompanionArmor(companion.id);
      companionArmor.forEach(armor => {
        if (armor.durability > 0) {
          equipmentDefenseBonus += armor.defense;
        }
      });
      
      const finalHp = Math.floor(baseHp) + researchHpBonus;
      const finalAttack = Math.floor(baseAttack) + equipmentAttackBonus + researchAttackBonus;
      const finalDefense = Math.floor(baseDefense) + equipmentDefenseBonus + researchDefenseBonus;
      
      // Debug-Ausgabe für mystische und göttliche Begleiter
      if (isMysticDivine || isGodlyLegendary) {
        console.log(`💪 ${companion.name}: HP=${finalHp}, Attack=${finalAttack} (Base: ${Math.floor(baseAttack)}, Equipment: +${equipmentAttackBonus}, Research: +${researchAttackBonus}), Defense=${finalDefense} (Base: ${Math.floor(baseDefense)}, Equipment: +${equipmentDefenseBonus}, Research: +${researchDefenseBonus})`);
      }
      
      // Debug-Ausgabe für Ausrüstung und Forschung
      if (equipmentAttackBonus > 0 || equipmentDefenseBonus > 0 || researchAttackBonus > 0 || researchDefenseBonus > 0) {
        console.log(`⚔️ ${companion.name} Boni: Equipment +${equipmentAttackBonus} Attack/+${equipmentDefenseBonus} Defense, Research +${researchAttackBonus} Attack/+${researchDefenseBonus} Defense`);
      }
      
      return {
        ...companion,
        currentHp: finalHp,
        maxHp: finalHp,
        attack: finalAttack,
        defense: finalDefense,
        battleLevel: finalLevel,
        battleExperience: companion.battleExperience || 0,
      };
    }
  };

  // Generiere wildes Gegner-Team
  const generateEnemyTeam = (): BattleCompanion[] => {
    const enemyTypes: Companion['type'][] = ['common', 'rare', 'epic', 'legendary'];
    const team: BattleCompanion[] = [];
    
    for (let i = 0; i < 5; i++) {
      const randomType = enemyTypes[Math.floor(Math.random() * enemyTypes.length)];
      const wildCompanion: Companion = {
        id: `wild_${Date.now()}_${i}`,
        name: `Wilder ${getWildName(randomType)}`,
        type: randomType,
        icon: getWildIcon(randomType),
        description: `Ein wilder Begleiter aus der Arena`,
        abilities: generateWildAbilities(randomType),
        discoveredAt: new Date().toISOString(),
        battleLevel: Math.max(1, Math.floor(arenaLevel * 0.7) + Math.floor(Math.random() * 2)),
        battleExperience: 0,
      };
      
      team.push(toBattleCompanion(wildCompanion, true));
    }
    
    return team;
  };

  const getWildName = (type: Companion['type']): string => {
    const names = {
      common: ['Schatten-Wolf', 'Stein-Bär', 'Wind-Katze', 'Feuer-Fuchs'],
      rare: ['Kristall-Drache', 'Blitz-Tiger', 'Eis-Adler', 'Sturm-Löwe'],
      epic: ['Void-Dämon', 'Licht-Phoenix', 'Schatten-Sphinx', 'Plasma-Bestie'],
      legendary: ['Uralter Titan', 'Kosmischer Wächter', 'Zeitlord', 'Dimensionsherr']
    };
    const typeNames = names[type];
    return typeNames[Math.floor(Math.random() * typeNames.length)];
  };

  const getWildIcon = (type: Companion['type']): string => {
    const icons = {
      common: ['🐺', '🐻', '🐱', '🦊'],
      rare: ['🐉', '🐅', '🦅', '🦁'],
      epic: ['👹', '🔥', '👁️', '⚡'],
      legendary: ['🌌', '⭐', '🌟', '💫']
    };
    const typeIcons = icons[type];
    return typeIcons[Math.floor(Math.random() * typeIcons.length)];
  };

  const generateWildAbilities = (type: Companion['type']) => {
    const abilityCount = { common: 1, rare: 2, epic: 3, legendary: 4 }[type];
    const abilities = [];
    
    const allAbilityTypes = [
      'clickMultiplier', 'coinPerSecond', 'criticalHit', 'healing', 
      'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'
    ];
    
    const usedTypes = new Set();
    
    for (let i = 0; i < abilityCount; i++) {
      let abilityType;
      do {
        abilityType = allAbilityTypes[Math.floor(Math.random() * allAbilityTypes.length)];
      } while (usedTypes.has(abilityType));
      
      usedTypes.add(abilityType);
      
      let value;
      switch (abilityType) {
        case 'criticalHit':
        case 'counterAttack':
        case 'lifeSteal':
          value = Math.floor(Math.random() * 15) + 3;
          break;
        case 'healing':
          value = Math.floor(Math.random() * 12) + 5;
          break;
        case 'shield':
        case 'armorPenetration':
          value = Math.floor(Math.random() * 10) + 3;
          break;
        default:
          value = Math.floor(Math.random() * 30) + 5;
      }
      
      abilities.push({
        type: abilityType as any,
        value,
        description: `Wilde ${abilityType}`
      });
    }
    
    return abilities;
  };

  // Team-Auswahl
  const handleCompanionSelect = (companion: Companion) => {
    if (selectedTeam.find(c => c.id === companion.id)) {
      setSelectedTeam(prev => prev.filter(c => c.id !== companion.id));
    } else if (selectedTeam.length < 5) {
      setSelectedTeam(prev => [...prev, companion]);
    }
  };

  // Schnellauswahl-Funktionen
  const selectRandomTeam = () => {
    const availableCompanions = companions.discovered.filter(c => !trainingCompanions.includes(c.id));
    const randomTeam: Companion[] = [];
    
    for (let i = 0; i < 5 && availableCompanions.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * availableCompanions.length);
      randomTeam.push(availableCompanions[randomIndex]);
      availableCompanions.splice(randomIndex, 1);
    }
    
    setSelectedTeam(randomTeam);
  };

  const selectBestTeam = () => {
    const availableCompanions = companions.discovered.filter(c => !trainingCompanions.includes(c.id));
    const companionsWithPower = availableCompanions.map(companion => {
      const battleComp = toBattleCompanion(companion);
      const totalPower = battleComp.attack + battleComp.defense + (battleComp.maxHp / 2);
      return { companion, power: totalPower };
    });
    
    const bestCompanions = companionsWithPower
      .sort((a, b) => b.power - a.power)
      .slice(0, 5)
      .map(item => item.companion);
    
    setSelectedTeam(bestCompanions);
  };

  const addBestCompanionsToTraining = () => {
    const availableCompanions = companions.discovered.filter(c => !trainingCompanions.includes(c.id));
    const companionsWithPower = availableCompanions.map(companion => {
      const battleComp = toBattleCompanion(companion);
      const totalPower = battleComp.attack + battleComp.defense + (battleComp.maxHp / 2);
      return { companion, power: totalPower };
    });
    
    const maxCapacity = getMaxTrainingCapacity();
    const bestCompanions = companionsWithPower
      .sort((a, b) => b.power - a.power)
      .slice(0, maxCapacity - trainingCompanions.length) // Nur so viele wie noch Platz ist
      .map(item => item.companion);
    
    // Füge die besten Begleiter zum Training hinzu
    bestCompanions.forEach(companion => {
      if (trainingCompanions.length < maxCapacity) {
        addToTraining(companion.id);
      }
    });
  };

  // Gegner-Team für Analyse generieren
  const previewEnemyTeam = generateEnemyTeam();

  // Team-Stärke berechnen
  const calculateTeamPower = (team: BattleCompanion[]): number => {
    return team.reduce((total, companion) => {
      return total + companion.attack + companion.defense + (companion.maxHp / 2);
    }, 0);
  };

  // Gewinnchancen berechnen
  const calculateWinChance = (): number => {
    if (selectedTeam.length !== 5) return 0;
    
    const playerTeamBattle = selectedTeam.map(c => toBattleCompanion(c));
    const playerPower = calculateTeamPower(playerTeamBattle);
    const enemyPower = calculateTeamPower(previewEnemyTeam);
    
    // Basis-Gewinnchance basierend auf Stärke-Verhältnis
    const powerRatio = playerPower / (enemyPower + 1);
    let baseChance = Math.min(95, Math.max(5, powerRatio * 50));
    
    // Bonus für Team-Synergien (verschiedene Fähigkeiten)
    const uniqueAbilities = new Set(playerTeamBattle.flatMap(c => c.abilities.map(a => a.type)));
    const synergyBonus = Math.min(15, uniqueAbilities.size * 2);
    
    return Math.min(95, Math.max(5, baseChance + synergyBonus));
  };

  // Kampf starten
  const startBattle = (teamToUse?: Companion[]) => {
    const team = teamToUse || selectedTeam;
    if (team.length !== 5) return;
    
    // Speichere das verwendete Team für Auto-Kampf
    setLastUsedTeam(team);
    
    const playerTeam = team.map(c => toBattleCompanion(c));
    const enemyTeam = generateEnemyTeam();
    
    setCurrentBattle({
      playerTeam,
      enemyTeam,
      turn: 0,
      phase: 'battle'
    });
    
    setBattleLog([]);
    setBattleResult(null);
    
    // Starte Kampf-Simulation
    simulateBattle(playerTeam, enemyTeam);
  };

  // Hilfsfunktion für Kampf-Fähigkeiten
  const getAbilityValue = (companion: BattleCompanion, abilityType: string): number => {
    const ability = companion.abilities.find(a => a.type === abilityType);
    return ability ? ability.value : 0;
  };

  // Kampf-Simulation
  const simulateBattle = async (playerTeam: BattleCompanion[], enemyTeam: BattleCompanion[]) => {
    const log: string[] = [];
    let turn = 0;
    
    while (playerTeam.some(c => c.currentHp > 0) && enemyTeam.some(c => c.currentHp > 0)) {
      turn++;
      log.push(`🔥 Runde ${turn}`);
      setBattleLog([...log]);
      // Update turn counter and battle state in real-time
      setCurrentBattle(prev => prev ? { 
        ...prev, 
        turn,
        playerTeam: [...playerTeam],
        enemyTeam: [...enemyTeam]
      } : null);
      // Add delay to make battle visible
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Heilung am Rundenbeginn
      [...playerTeam, ...enemyTeam].forEach(companion => {
        if (companion.currentHp > 0) {
          const healing = getAbilityValue(companion, 'healing');
          if (healing > 0) {
            const healAmount = Math.floor(healing);
            companion.currentHp = Math.min(companion.maxHp, companion.currentHp + healAmount);
            if (healAmount > 0) {
              log.push(`💚 ${companion.name} heilt sich um ${healAmount} HP`);
              setBattleLog([...log]);
            }
          }
        }
      });
      // Spieler-Team Angriff
      for (const attacker of playerTeam) {
        if (attacker.currentHp <= 0) continue;
        const aliveEnemies = enemyTeam.filter(c => c.currentHp > 0);
        if (aliveEnemies.length === 0) break;
        const target = aliveEnemies[Math.floor(Math.random() * aliveEnemies.length)];
        // Berechne Schaden mit Kampf-Fähigkeiten
        let baseDamage = attacker.attack - target.defense;
        const armorPen = getAbilityValue(attacker, 'armorPenetration');
        const shield = getAbilityValue(target, 'shield');
        const critChance = getAbilityValue(attacker, 'criticalHit');
        baseDamage += armorPen;
        baseDamage = Math.max(1, baseDamage - shield);
        let isCrit = false;
        if (Math.random() * 100 < critChance) {
          baseDamage *= 2;
          isCrit = true;
        }
        const finalDamage = Math.max(1, baseDamage + Math.floor(Math.random() * 10) - 5);
        const lifeSteal = getAbilityValue(attacker, 'lifeSteal');
        if (lifeSteal > 0) {
          const healAmount = Math.floor(finalDamage * (lifeSteal / 100));
          attacker.currentHp = Math.min(attacker.maxHp, attacker.currentHp + healAmount);
          if (healAmount > 0) {
            log.push(`🩸 ${attacker.name} stiehlt ${healAmount} HP`);
            setBattleLog([...log]);
            // Update battle state after life steal
            setCurrentBattle(prev => prev ? { 
              ...prev, 
              playerTeam: [...playerTeam],
              enemyTeam: [...enemyTeam]
            } : null);
          }
        }
        target.currentHp = Math.max(0, target.currentHp - finalDamage);
        log.push(`⚔️ ${attacker.name} greift ${target.name} an (-${finalDamage} HP)${isCrit ? ' 💥 KRITISCH!' : ''}`);
        setBattleLog([...log]);
        // Update battle state immediately after damage
        setCurrentBattle(prev => prev ? { 
          ...prev, 
          playerTeam: [...playerTeam],
          enemyTeam: [...enemyTeam]
        } : null);
        if (target.currentHp > 0) {
          const counterChance = getAbilityValue(target, 'counterAttack');
          if (Math.random() * 100 < counterChance) {
            const counterDamage = Math.max(1, Math.floor(target.attack * 0.5));
            attacker.currentHp = Math.max(0, attacker.currentHp - counterDamage);
            log.push(`🔄 ${target.name} kontert zurück (-${counterDamage} HP)`);
            setBattleLog([...log]);
            // Update battle state after counter attack
            setCurrentBattle(prev => prev ? { 
              ...prev, 
              playerTeam: [...playerTeam],
              enemyTeam: [...enemyTeam]
            } : null);
          }
        }
        if (target.currentHp === 0) {
          log.push(`💀 ${target.name} wurde besiegt!`);
          setBattleLog([...log]);
        }
      }
      // Gegner-Team Angriff
      for (const attacker of enemyTeam) {
        if (attacker.currentHp <= 0) continue;
        const alivePlayers = playerTeam.filter(c => c.currentHp > 0);
        if (alivePlayers.length === 0) break;
        const target = alivePlayers[Math.floor(Math.random() * alivePlayers.length)];
        let baseDamage = attacker.attack - target.defense;
        const armorPen = getAbilityValue(attacker, 'armorPenetration');
        const shield = getAbilityValue(target, 'shield');
        const critChance = getAbilityValue(attacker, 'criticalHit');
        baseDamage += armorPen;
        baseDamage = Math.max(1, baseDamage - shield);
        let isCrit = false;
        if (Math.random() * 100 < critChance) {
          baseDamage *= 2;
          isCrit = true;
        }
        const finalDamage = Math.max(1, baseDamage + Math.floor(Math.random() * 10) - 5);
        const lifeSteal = getAbilityValue(attacker, 'lifeSteal');
        if (lifeSteal > 0) {
          const healAmount = Math.floor(finalDamage * (lifeSteal / 100));
          attacker.currentHp = Math.min(attacker.maxHp, attacker.currentHp + healAmount);
          if (healAmount > 0) {
            log.push(`🩸 ${attacker.name} stiehlt ${healAmount} HP`);
            setBattleLog([...log]);
            // Update battle state after life steal
            setCurrentBattle(prev => prev ? { 
              ...prev, 
              playerTeam: [...playerTeam],
              enemyTeam: [...enemyTeam]
            } : null);
          }
        }
        target.currentHp = Math.max(0, target.currentHp - finalDamage);
        log.push(`⚔️ ${attacker.name} greift ${target.name} an (-${finalDamage} HP)${isCrit ? ' 💥 KRITISCH!' : ''}`);
        setBattleLog([...log]);
        // Update battle state immediately after damage
        setCurrentBattle(prev => prev ? { 
          ...prev, 
          playerTeam: [...playerTeam],
          enemyTeam: [...enemyTeam]
        } : null);
        if (target.currentHp > 0) {
          const counterChance = getAbilityValue(target, 'counterAttack');
          if (Math.random() * 100 < counterChance) {
            const counterDamage = Math.max(1, Math.floor(target.attack * 0.5));
            attacker.currentHp = Math.max(0, attacker.currentHp - counterDamage);
            log.push(`🔄 ${target.name} kontert zurück (-${counterDamage} HP)`);
            setBattleLog([...log]);
            // Update battle state after counter attack
            setCurrentBattle(prev => prev ? { 
              ...prev, 
              playerTeam: [...playerTeam],
              enemyTeam: [...enemyTeam]
            } : null);
          }
        }
        if (target.currentHp === 0) {
          log.push(`💀 ${target.name} wurde besiegt!`);
          setBattleLog([...log]);
        }
      }
      // Automatischer Verlust bei Runde 10
      if (turn >= 10) {
        log.push(`⏰ Runde 10 erreicht - Automatische Niederlage!`);
        setBattleLog([...log]);
        // Alle Spieler-Begleiter auf 0 HP setzen für automatischen Verlust
        playerTeam.forEach(companion => companion.currentHp = 0);
        break;
      }
    }
    
    const playerWins = playerTeam.some(c => c.currentHp > 0);
    const winner = playerWins ? 'player' : 'enemy';
    
    log.push(`\n🏆 ${winner === 'player' ? 'Sieg!' : 'Niederlage!'}`);
    
    const baseReward = arenaLevel * 100;
    const rewards = {
      coins: playerWins ? baseReward * 2 : Math.floor(baseReward * 0.5),
      gems: playerWins ? Math.floor(arenaLevel * 0.5) : 0,
      experience: baseReward
    };

    // 10% Chance einen wilden Begleiter zu fangen (nur bei Sieg)
    let capturedCompanion: Companion | null = null;
    const captureRoll = Math.random();
    console.log(`🎲 Arena Capture Roll: ${captureRoll.toFixed(3)} (Benötigt < 0.10)`);
    
    if (playerWins && captureRoll < 0.50) { // Temporär auf 50% erhöht für Testing
      // Neue Wahrscheinlichkeiten: Common 70%, Rare 29%, Epic 1%, Legendary 0%
      const random = Math.random();
      let selectedType: Companion['type'];
      
      if (random < 0.70) {
        selectedType = 'common'; // 70% Chance
      } else if (random < 0.99) {
        selectedType = 'rare'; // 29% Chance (0.70 + 0.29 = 0.99)
      } else {
        selectedType = 'epic'; // 1% Chance (0.99 + 0.01 = 1.0)
      }
      
      capturedCompanion = {
        id: `captured_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: `Gefangener ${getWildName(selectedType)}`,
        type: selectedType,
        icon: getWildIcon(selectedType),
        description: `Ein wilder Begleiter, der in der Arena gefangen wurde`,
        abilities: generateWildAbilities(selectedType),
        discoveredAt: new Date().toISOString(),
        level: 1,
        experience: 0,
        battleLevel: 1,
        battleExperience: 0,
      };
      
      // Füge den gefangenen Begleiter zur Sammlung hinzu
      addCompanionToCollection(capturedCompanion);
      log.push(`🎣 ${capturedCompanion.name} wurde gefangen!`);
      console.log(`✅ Begleiter gefangen: ${capturedCompanion.name} (${capturedCompanion.type})`);
    }
    
    if (playerWins) {
      playerTeam.forEach(companion => {
        if (companion.currentHp > 0) {
          // Battle Level XP
          const battleXpGain = arenaLevel * 25;
          const newBattleExperience = companion.battleExperience + battleXpGain;
          let newBattleLevel = companion.battleLevel;
          let finalBattleExperience = newBattleExperience;
          
          if (newBattleExperience >= (companion.battleLevel * 100)) {
            newBattleLevel++;
            finalBattleExperience = newBattleExperience - (companion.battleLevel * 100);
            log.push(`⚔️ ${companion.name} erreicht Battle-Level ${newBattleLevel}!`);
            log.push(`🗡️ Kampf-Fähigkeiten wurden verstärkt!`);
            
            if (Math.random() < 0.3) {
              const battleAbilities = ['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'];
              const existingTypes = companion.abilities.map(a => a.type);
              const availableAbilities = battleAbilities.filter(type => !existingTypes.includes(type as any));
              
              if (availableAbilities.length > 0) {
                const newAbilityType = availableAbilities[Math.floor(Math.random() * availableAbilities.length)];
                let value;
                
                switch (newAbilityType) {
                  case 'criticalHit':
                  case 'counterAttack':
                  case 'lifeSteal':
                    value = Math.floor(Math.random() * 15) + 5;
                    break;
                  case 'healing':
                    value = Math.floor(Math.random() * 15) + 5;
                    break;
                  case 'shield':
                  case 'armorPenetration':
                    value = Math.floor(Math.random() * 10) + 3;
                    break;
                  default:
                    value = 10;
                }
                
                const newAbility: CompanionAbility = {
                  type: newAbilityType as any,
                  value,
                  description: `Kampf-Fähigkeit: ${newAbilityType}`
                };
                
                addCompanionAbility(companion.id, newAbility);
                log.push(`🎯 ${companion.name} erlernt neue Fähigkeit: ${newAbilityType}!`);
              }
            }
          }
          
          // Normales Level XP
          const normalXpGain = arenaLevel * 15;
          const currentNormalLevel = companion.level || 1;
          const currentNormalExperience = companion.experience || 0;
          const newNormalExperience = currentNormalExperience + normalXpGain;
          let newNormalLevel = currentNormalLevel;
          let finalNormalExperience = newNormalExperience;
          
          if (newNormalExperience >= (currentNormalLevel * 100)) {
            newNormalLevel++;
            finalNormalExperience = newNormalExperience - (currentNormalLevel * 100);
            log.push(`✨ ${companion.name} erreicht normales Level ${newNormalLevel}!`);
            log.push(`💪 Standard-Fähigkeiten wurden verstärkt!`);
          }
          
          // Update beide Level-Systeme
          updateCompanionBattleStats(companion.id, newBattleLevel, finalBattleExperience);
          updateCompanionNormalStats(companion.id, newNormalLevel, finalNormalExperience);
          log.push(`⚔️ ${companion.name} erhält ${battleXpGain} Kampf-XP (Battle Level ${newBattleLevel}) und ${normalXpGain} normale XP (Level ${newNormalLevel})`);
        }
      });
    }
    
    setBattleLog(log);
    setBattleResult({
      winner,
      playerTeam,
      enemyTeam,
      rewards,
      capturedCompanion
    });
    
    setCurrentBattle(prev => prev ? { ...prev, phase: 'result' } : null);
    
    addCoins(rewards.coins);
    addGems(rewards.gems);
    addExperience(rewards.experience);
    
    if (playerWins) {
      setArenaLevel(arenaLevel + 1);
    }

    // Haltbarkeit von Rüstungen und Waffen nach Arena-Kampf reduzieren
    const companionIds = playerTeam.map(companion => companion.id);
    reduceEquipmentDurability(companionIds);
  };

  const endBattle = (stopAuto: boolean = true) => {
    setCurrentBattle(null);
    setBattleResult(null);
    setBattleLog([]);
    setSelectedTeam([]);
    if (stopAuto) {
      setAutoBattle(false);
    }
  };

  // Auto-Kampf starten
  const startAutoBattle = () => {
    if (lastUsedTeam.length !== 5) return;
    setAutoBattle(true);
    startBattle(lastUsedTeam);
  };

  // Auto-Kampf stoppen - sofort
  const stopAutoBattle = () => {
    setAutoBattle(false);
    setAutoBattleSpeed(1); // Reset speed
    // Sofort zur Arena zurückkehren, wenn ein Kampf läuft
    if (currentBattle) {
      endBattle(true);
    }
  };

  // Auto Speed Boost aktivieren (x2 oder x4 basierend auf Arena Level)
  const startAutoBattleSpeedBoost = () => {
    const speedMultiplier = arenaLevel >= 150 ? 4 : 2;
    console.log(`🚀 Auto (x${speedMultiplier}) Button geklickt`);
    
    setAutoBattleSpeed(speedMultiplier);
    setAutoBattle(true);
    if (lastUsedTeam.length === 5) {
      startBattle(lastUsedTeam);
    }

    // Endzeit für Speed Boost setzen (10 Minuten)
    const endTime = Date.now() + (10 * 60 * 1000);
    setSpeedBoostEndTime(endTime);

    // Timer für 10 Minuten - dann Geschwindigkeit zurücksetzen
    setTimeout(() => {
      console.log(`⏰ Auto (x${speedMultiplier}) Boost abgelaufen`);
      setAutoBattleSpeed(1);
      setSpeedBoostEndTime(null);
      // Auto-Kampf stoppen wenn noch aktiv
      if (autoBattle) {
        setAutoBattle(false);
      }
    }, 10 * 60 * 1000); // 10 Minuten
  };

  // Auto-Kampf-Logik - wird nur einmal ausgeführt wenn battleResult sich ändert
  React.useEffect(() => {
    if (battleResult && autoBattle) {
      if (battleResult.winner === 'player') {
        // Bei Sieg: Nächsten Kampf nach kurzer Pause starten
        // Geschwindigkeit berücksichtigen: x2 = halbe Wartezeit, x4 = viertel Wartezeit
        const waitTime = autoBattleSpeed === 4 ? 750 : autoBattleSpeed === 2 ? 1500 : 3000;
        const timer = setTimeout(() => {
          // Prüfen ob Auto-Kampf noch aktiv ist
          if (autoBattle && lastUsedTeam.length === 5) {
            startBattle(lastUsedTeam);
          }
        }, waitTime);
        
        return () => clearTimeout(timer);
      } else {
        // Bei Niederlage: Auto-Kampf stoppen
        setAutoBattle(false);
        setAutoBattleSpeed(1); // Reset speed
      }
    }
  }, [battleResult, autoBattle, autoBattleSpeed]); // autoBattleSpeed als Dependency hinzugefügt

  const getTrainingCompanion = (id: string) => {
    return companions.discovered.find(c => c.id === id);
  };

  // Haltbarkeit von Rüstungen und Waffen nach Arena-Kampf reduzieren
  const reduceEquipmentDurability = (companionIds: string[]) => {
    const durabilityLoss = {
      armor: 5, // 5 Haltbarkeit pro Kampf für Rüstungen
      weapon: 3, // 3 Haltbarkeit pro Kampf für Waffen
    };

    companionIds.forEach(companionId => {
      // Rüstungen reduzieren
      const companionArmor = getCompanionArmor(companionId);
      companionArmor.forEach(armor => {
        if (armor.durability > 0) {
          reduceArmorDurability(armor.id, durabilityLoss.armor);
        }
      });

      // Waffen reduzieren
      const companionWeapons = getCompanionWeapons(companionId);
      companionWeapons.forEach(weapon => {
        if (weapon.durability > 0) {
          reduceWeaponDurability(weapon.id, durabilityLoss.weapon);
        }
      });
    });
  };

  // Wenn Kampf läuft - Mobile-optimierte Schlacht-UI
  if (currentBattle) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900 via-orange-900 to-red-800 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-red-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        {/* Header */}
        <div className="relative z-10 p-4 bg-black/20 backdrop-blur-sm border-b border-red-400/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">

              <div>
                <h3 className="text-xl font-bold text-white">Arena-Kampf</h3>
                <div className="text-red-300 text-sm">Level {arenaLevel}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-white text-sm">Runde {currentBattle.turn}</div>
              <div className="text-red-300 text-xs">
                {autoBattle ? `🤖 Auto-Kampf${autoBattleSpeed > 1 ? ` (x${autoBattleSpeed})` : ''}` : 'Normal'}
                {autoBattleSpeed > 1 && speedBoostEndTime && (
                  <div className="text-indigo-300 text-xs">
                    ⏱️ {Math.floor(getSpeedBoostTimeRemaining() / 60)}:{(getSpeedBoostTimeRemaining() % 60).toString().padStart(2, '0')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Battle Phase */}
        {currentBattle.phase === 'battle' && (
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 text-center">
            <motion.div 
              className="text-8xl mb-6"
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              
            </motion.div>
            <motion.div 
              className="text-2xl font-bold text-white mb-4"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              KAMPF LÄUFT
            </motion.div>

            {/* Battle Progress */}
            <div className="mt-2 w-full max-w-md">
              <div className="bg-black/30 rounded-full h-2 overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-red-500 to-orange-500"
                  animate={{ width: ['0%', '100%'] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </div>
            </div>
            {/* Toggle zwischen Kampf-Teilnehmern und Kampf-Verlauf */}
            <div className="mt-4 w-full">
              {/* Toggle Buttons */}
              <div className="flex bg-black/20 rounded-xl p-1 border border-gray-500/30 mb-3">
                <motion.button
                  onClick={() => setBattleView('participants')}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                    battleView === 'participants'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  👥 Teilnehmer
                </motion.button>
                <motion.button
                  onClick={() => setBattleView('log')}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                    battleView === 'log'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  📜 Verlauf
                </motion.button>
              </div>

              {/* Kampf-Teilnehmer Anzeige - Volle Modal-Breite */}
              {battleView === 'participants' && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-black/40 rounded-2xl border-2 border-gray-400/50 p-4 shadow-2xl w-full"
                >
                  <div className="text-white font-bold mb-3 flex items-center gap-2 text-base">
                    <span className="text-lg">⚔️</span> Kampf-Teilnehmer
                    <span className="text-sm text-gray-300">({(currentBattle as any)?.playerTeam?.length || 0} vs {(currentBattle as any)?.enemyTeam?.length || 0})</span>
                  </div>
                  
                  {/* Vergrößerte Kampf-Teilnehmer - Mobile */}
                  <div className="space-y-3">
                    {/* Spieler-Team - Vergrößert */}
                    <div>
                      <div className="text-blue-300 text-sm font-bold mb-2 flex items-center gap-1">👥 Dein Team</div>
                      <div className="grid grid-cols-5 gap-1 auto-rows-fr">
                        {(currentBattle as any)?.playerTeam?.map((companion: BattleCompanion, idx: number) => (
                          <div key={idx} className={`relative rounded-lg p-2 border-2 transition-all duration-300 hover:scale-105 w-full h-full ${
                            companion.currentHp <= 0 
                              ? 'bg-gradient-to-b from-gray-700/40 to-gray-800/40 border-gray-500/50 opacity-50' 
                              : companion.currentHp / companion.maxHp > 0.5 
                                ? 'bg-gradient-to-b from-blue-500/40 to-blue-600/40 border-blue-400/60 shadow-lg shadow-blue-500/20' 
                                : companion.currentHp / companion.maxHp > 0.25 
                                  ? 'bg-gradient-to-b from-yellow-500/40 to-orange-500/40 border-yellow-400/60 shadow-lg shadow-yellow-500/20' 
                                  : 'bg-gradient-to-b from-red-500/40 to-red-600/40 border-red-400/60 shadow-lg shadow-red-500/20'
                          }`}>
                            {/* Status-Indikator */}
                            <div className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${
                              companion.currentHp <= 0 ? 'bg-gray-400' : 'bg-green-400 animate-pulse'
                            }`}></div>
                            
                            {/* Rarity-Indikator */}
                            <div className={`absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full ${
                              companion.type === 'legendary' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                              companion.type === 'epic' ? 'bg-gradient-to-r from-purple-400 to-pink-500' :
                              companion.type === 'rare' ? 'bg-gradient-to-r from-blue-400 to-cyan-500' :
                              'bg-gradient-to-r from-gray-400 to-gray-500'
                            }`}></div>
                            
                            <div className="text-center">
                                                          {/* Icon und Name kompakt */}
                            <div className="text-xl mb-1 drop-shadow-lg">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-6 h-6 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </div>
                              <div className="text-[6px] text-white font-bold mb-2 drop-shadow-sm leading-tight break-words px-1 w-full flex justify-center items-center text-center min-h-[2.4rem]">
                                {companion.name}
                              </div>
                              
                              {/* Lebensbalken kompakt */}
                              <div className="w-full bg-black/50 rounded-full h-1.5 overflow-hidden border border-white/20 mb-1">
                                <motion.div 
                                  className={`h-1.5 rounded-full bg-gradient-to-r ${
                                    companion.currentHp / companion.maxHp > 0.5 ? 'from-green-400 to-emerald-400' :
                                    companion.currentHp / companion.maxHp > 0.25 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-pink-400'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.max(0, (companion.currentHp / companion.maxHp) * 100)}%` }}
                                  transition={{ duration: 0.5, ease: "easeOut" }}
                                />
                              </div>
                              
                              {/* HP kompakt */}
                              <div className="text-[5px] text-white font-bold drop-shadow-sm w-full flex justify-center items-center text-center">
                                {formatNumber(Math.max(0, Math.round(companion.currentHp)))}/{formatNumber(companion.maxHp)}
                              </div>
                            </div>
                          </div>
                        )) || []}
                      </div>
                    </div>

                    {/* Gegner-Team - Vergrößert */}
                    <div>
                      <div className="text-red-300 text-sm font-bold mb-2 flex items-center gap-1">🐺 Gegner</div>
                      <div className="grid grid-cols-5 gap-1 auto-rows-fr">
                        {(currentBattle as any)?.enemyTeam?.map((enemy: BattleCompanion, idx: number) => (
                          <div key={idx} className={`relative rounded-lg p-2 border-2 transition-all duration-300 hover:scale-105 w-full h-full ${
                            enemy.currentHp <= 0 
                              ? 'bg-gradient-to-b from-gray-700/40 to-gray-800/40 border-gray-500/50 opacity-50' 
                              : enemy.currentHp / enemy.maxHp > 0.5 
                                ? 'bg-gradient-to-b from-red-500/40 to-red-600/40 border-red-400/60 shadow-lg shadow-red-500/20' 
                                : enemy.currentHp / enemy.maxHp > 0.25 
                                  ? 'bg-gradient-to-b from-orange-500/40 to-red-500/40 border-orange-400/60 shadow-lg shadow-orange-500/20' 
                                  : 'bg-gradient-to-b from-red-600/40 to-red-700/40 border-red-500/60 shadow-lg shadow-red-600/20'
                          }`}>
                            {/* Status-Indikator */}
                            <div className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${
                              enemy.currentHp <= 0 ? 'bg-gray-400' : 'bg-red-400 animate-pulse'
                            }`}></div>
                            
                            {/* Rarity-Indikator */}
                            <div className={`absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full ${
                              enemy.type === 'legendary' ? 'bg-gradient-to-r from-yellow-400 to-orange-500' :
                              enemy.type === 'epic' ? 'bg-gradient-to-r from-purple-400 to-pink-500' :
                              enemy.type === 'rare' ? 'bg-gradient-to-r from-blue-400 to-cyan-500' :
                              'bg-gradient-to-r from-gray-400 to-gray-500'
                            }`}></div>
                            
                            <div className="text-center">
                                                          {/* Icon und Name kompakt */}
                            <div className="text-xl mb-1 drop-shadow-lg">
                              {enemy.icon.startsWith('data:') ? (
                                <img 
                                  src={enemy.icon} 
                                  alt={enemy.name}
                                  className="w-6 h-6 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                enemy.icon
                              )}
                            </div>
                              <div className="text-[6px] text-white font-bold mb-2 drop-shadow-sm leading-tight break-words px-1 w-full flex justify-center items-center text-center min-h-[2.4rem]">
                                {enemy.name}
                              </div>
                              
                              {/* Lebensbalken kompakt */}
                              <div className="w-full bg-black/50 rounded-full h-1.5 overflow-hidden border border-white/20 mb-1">
                                <motion.div 
                                  className={`h-1.5 rounded-full bg-gradient-to-r ${
                                    enemy.currentHp / enemy.maxHp > 0.5 ? 'from-green-400 to-emerald-400' :
                                    enemy.currentHp / enemy.maxHp > 0.25 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-pink-400'
                                  }`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.max(0, (enemy.currentHp / enemy.maxHp) * 100)}%` }}
                                  transition={{ duration: 0.5, ease: "easeOut" }}
                                />
                              </div>
                              
                              {/* HP kompakt */}
                              <div className="text-[5px] text-white font-bold drop-shadow-sm w-full flex justify-center items-center text-center">
                                {formatNumber(Math.max(0, Math.round(enemy.currentHp)))}/{formatNumber(enemy.maxHp)}
                              </div>
                            </div>
                          </div>
                        )) || []}
                      </div>
                    </div>
                  </div>
                  
                  {/* Wegrennen Button - nur bei Auto (x4) */}
                  {autoBattle && autoBattleSpeed === 4 && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-3"
                    >
                      <motion.button
                        onClick={() => {
                          console.log('🏃‍♂️ Wegrennen Button geklickt');
                          // Simuliere einen Sieg beim Wegrennen
                          const mockBattleResult: BattleResult = {
                            winner: 'player',
                            playerTeam: (currentBattle as any)?.playerTeam || [],
                            enemyTeam: (currentBattle as any)?.enemyTeam || [],
                            rewards: {
                              coins: arenaLevel * 50,
                              gems: Math.floor(arenaLevel * 0.3),
                              experience: arenaLevel * 25
                            }
                          };
                          setBattleResult(mockBattleResult);
                          
                          // Belohnungen hinzufügen
                          addCoins(mockBattleResult.rewards.coins);
                          addGems(mockBattleResult.rewards.gems);
                          addExperience(mockBattleResult.rewards.experience);
                          
                          endBattle(true);
                          setShowVictoryModal(true);
                        }}
                        className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white py-2 px-4 rounded-lg font-bold text-sm border border-red-400 shadow-lg shadow-red-500/25"
                        whileTap={{ scale: 0.95 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        🏃‍♂️ Wegrennen
                      </motion.button>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Kampf-Verlauf Anzeige */}
              {battleView === 'log' && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-black/30 rounded-2xl border border-gray-500/30 max-h-40 overflow-y-auto p-3"
                >
                  <div className="text-white font-bold mb-2 flex items-center gap-2">
                    <span>📜</span> Kampf-Verlauf
                  </div>
                  <div className="space-y-1 text-sm">
                    {battleLog.length === 0 && (
                      <div className="text-gray-400 italic">Der Kampf beginnt ...</div>
                    )}
                    {battleLog.slice(-10).map((entry, idx) => (
                      <div key={idx} className="text-gray-300 py-1 border-b border-gray-600/30 last:border-0">
                        {entry}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}

        {/* Battle Result */}
        {currentBattle.phase === 'result' && battleResult && (
          <div className="relative z-10 flex-1 p-4 space-y-4">
            {/* Kompakte Sieg/Niederlage Anzeige */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={`p-3 rounded-xl border ${
                battleResult.winner === 'player' 
                  ? 'bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-green-400/50' 
                  : 'bg-gradient-to-r from-red-600/20 to-rose-600/20 border-red-400/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <motion.div 
                    className="text-2xl"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: battleResult.winner === 'player' ? [0, 5, -5, 0] : [0, -5, 5, 0]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {battleResult.winner === 'player' ? '🏆' : '💀'}
                  </motion.div>
                  <div>
                    <div className="text-white font-bold text-sm">
                      {battleResult.winner === 'player' ? 'SIEG!' : 'NIEDERLAGE!'}
                    </div>
                    <div className="text-white/70 text-xs">
                      {battleResult.winner === 'player' ? 'Dein Team triumphiert!' : 'Dein Team wurde besiegt...'}
                    </div>
                  </div>
                </div>
                
                {/* Kompakte Belohnungen */}
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <div className="text-lg">💰</div>
                    <div className="text-yellow-300 text-xs">+{formatNumber(battleResult.rewards.coins)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg">💎</div>
                    <div className="text-purple-300 text-xs">+{formatNumber(battleResult.rewards.gems)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg">⭐</div>
                    <div className="text-blue-300 text-xs">+{formatNumber(battleResult.rewards.experience)}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Battle Log - Collapsible */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-black/30 rounded-2xl border border-gray-500/30"
            >
              <details className="group">
                <summary className="p-4 cursor-pointer flex items-center justify-between text-white font-medium">
                  <span>📜 Kampf-Verlauf</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 pb-4 max-h-40 overflow-y-auto">
                  <div className="space-y-1 text-sm">
                    {battleLog.map((entry, idx) => (
                      <div key={idx} className="text-gray-300 py-1 border-b border-gray-600/30 last:border-0">
                        {entry}
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-4"
            >
              <motion.button
                onClick={() => endBattle(true)}
                className="bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 px-4 rounded-2xl font-bold text-lg border border-gray-500 shadow-lg"
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.02 }}
              >
                🏠 Zurück
              </motion.button>
              
              {battleResult.winner === 'player' && (
                <motion.button
                  onClick={() => {
                    startBattle(lastUsedTeam);
                  }}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-4 rounded-2xl font-bold text-lg border border-green-400 shadow-lg shadow-green-500/25"
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                >
                  ⚔️ Weiter
                </motion.button>
              )}

              {/* Auto-Kampf Button - nur nach Sieg verfügbar */}
              {battleResult.winner === 'player' && (
                <motion.button
                  onClick={() => {
                    if (autoBattle) {
                      stopAutoBattle();
                    } else {
                      startAutoBattle();
                    }
                  }}
                  className={`bg-gradient-to-r ${
                    autoBattle 
                      ? 'from-red-600 to-rose-600 border-red-400' 
                      : 'from-purple-600 to-violet-600 border-purple-400'
                  } text-white py-4 px-4 rounded-2xl font-bold text-lg border shadow-lg`}
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                >
                  {autoBattle ? '⏹️ Stop' : '🤖 Auto'}
                </motion.button>
              )}

              {/* Auto Speed Boost Button - nur nach Sieg und ab Level 50 verfügbar */}
              {battleResult.winner === 'player' && arenaLevel >= 50 && (
                <motion.button
                  onClick={startAutoBattleSpeedBoost}
                  className={`bg-gradient-to-r text-white py-4 px-4 rounded-2xl font-bold text-lg border shadow-lg ${
                    arenaLevel >= 150 
                      ? 'from-red-600 to-orange-600 border-red-400 shadow-red-500/25' 
                      : 'from-indigo-600 to-purple-600 border-indigo-400 shadow-indigo-500/25'
                  }`}
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.02 }}
                >
                  🚀 Auto (x{arenaLevel >= 150 ? '4' : '2'})
                </motion.button>
              )}
            </motion.div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-800 via-orange-800 to-red-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-40 h-40 bg-orange-400 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-40 right-10 w-32 h-32 bg-red-400 rounded-full blur-3xl animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-yellow-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 p-4 bg-black/20 backdrop-blur-sm border-b border-red-400/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              className="text-4xl"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ⚔️
            </motion.div>
            <div>
              <h3 className="text-2xl font-bold text-white">Arena</h3>
              <div className="text-red-300 text-sm">Kampf-Zone</div>
            </div>
          </div>
          <div className="text-right">
            <div className="bg-red-600/30 rounded-full px-3 py-1 border border-red-400/50 mb-2">
              <span className="text-white font-bold">Level {arenaLevel}</span>
            </div>
            {getArenaDifficultyMultiplier() > 1.0 && (
              <div className="text-orange-300 text-xs">
                Schwierigkeit: +{Math.round((getArenaDifficultyMultiplier() - 1) * 100)}%
              </div>
            )}
            {getArenaResetCount() > 0 && (
              <div className="text-blue-300 text-xs">
                Resets: {getArenaResetCount()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab-Navigation */}
      <div className="relative z-10 mx-4 mt-4">
        <div className="flex bg-black/20 rounded-2xl p-1 border border-red-400/30">
          <motion.button
            onClick={() => setActiveTab('arena')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-lg transition-all ${
              activeTab === 'arena'
                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg'
                : 'text-red-300 hover:text-white'
            }`}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-xl">⚔️</span>
              Arena
            </div>
          </motion.button>
          
          <motion.button
            onClick={() => setActiveTab('barracks')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-lg transition-all ${
              activeTab === 'barracks'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                : 'text-red-300 hover:text-white'
            }`}
            whileTap={{ scale: 0.95 }}
          >
            <div className="flex items-center justify-center gap-2">
              <span className="text-xl">🏰</span>
              Kaserne
            </div>
          </motion.button>
        </div>
      </div>

      {/* Arena Tab */}
      {activeTab === 'arena' && !currentBattle && (
        <div className="relative z-10 mx-4 mt-4">
          {/* Verfügbare Begleiter */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl p-3 border border-purple-400/30"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-bold text-lg flex items-center gap-2">
                <span>👥</span>Begleiter ({selectedTeam.length}/5)
              </h4>
              {selectedTeam.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTeam([])}
                    className="text-red-400 text-sm px-2 py-1 rounded bg-red-500/20 border border-red-400/30"
                  >
                    Alle abwählen
                  </button>
                  {selectedTeam.length === 5 && (
                    <button
                      onClick={() => setShowBattleAnalysis(true)}
                      className="text-blue-400 text-sm px-2 py-1 rounded bg-blue-500/20 border border-blue-400/30"
                    >
                      🎯 Analyse
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Schnellauswahl-Buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <motion.button
                onClick={selectRandomTeam}
                disabled={companions.discovered.filter(c => !trainingCompanions.includes(c.id)).length < 5}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-white rounded-lg border border-purple-400/30 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>🎲</span>
                Zufällig
              </motion.button>
              
              <motion.button
                onClick={selectBestTeam}
                disabled={companions.discovered.filter(c => !trainingCompanions.includes(c.id)).length < 5}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-yellow-600/30 to-orange-600/30 text-white rounded-lg border border-yellow-400/30 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>⭐</span>
                Top 5
              </motion.button>
            </div>
            
            {/* Begleiter-Liste */}
            <div className="max-h-96 overflow-y-auto p-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
                {companions.discovered.map((companion, index) => {
                  const isSelected = selectedTeam.find(c => c.id === companion.id);
                  const isTraining = trainingCompanions.includes(companion.id);
                  const canSelect = !isSelected && selectedTeam.length < 5 && !isTraining;
                  const battleComp = toBattleCompanion(companion);
                  
                  return (
                    <motion.div
                      key={companion.id}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.02 * index }}
                      onClick={() => !isTraining && handleCompanionSelect(companion)}
                      className="relative group cursor-pointer"
                      whileHover={canSelect || isSelected ? { scale: 1.05, y: -2 } : {}}
                      whileTap={canSelect || isSelected ? { scale: 0.95 } : {}}
                    >
                      {/* Kompakte Begleiter-Karte */}
                      <div className={`
                        relative bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-md border border-white/50 hover:shadow-lg transition-all duration-300 overflow-hidden
                        ${isSelected 
                          ? 'ring-2 ring-green-400 ring-opacity-80 bg-green-500/10' 
                          : isTraining
                            ? 'opacity-75 cursor-not-allowed bg-blue-500/20'
                            : canSelect 
                              ? 'hover:bg-white/95' 
                              : 'opacity-50 cursor-not-allowed bg-gray-500/50'
                        }
                      `}>
                        {/* Rarity Glow-Effekt */}
                        <div className={`absolute inset-0 rounded-lg ${
                          companion.type === 'legendary' ? 'bg-gradient-to-br from-yellow-400/20 via-orange-400/20 to-red-400/20' :
                          companion.type === 'epic' ? 'bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-purple-400/20' :
                          companion.type === 'rare' ? 'bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-blue-400/20' :
                          'bg-gradient-to-br from-gray-400/20 via-gray-300/20 to-gray-400/20'
                        }`}></div>
                        
                        {/* Status-Indikatoren */}
                        {isSelected && (
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-0.5 -right-0.5 bg-green-500 rounded-full w-3 h-3 flex items-center justify-center text-white font-bold border border-white shadow-sm text-[8px] z-10"
                          >
                            ✓
                          </motion.div>
                        )}
                        
                        {isTraining && (
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-0.5 -left-0.5 bg-blue-500 rounded-full w-3 h-3 flex items-center justify-center text-white font-bold border border-white shadow-sm text-[8px] z-10"
                          >
                            🏰
                          </motion.div>
                        )}
                        
                        {/* Kompakte Begleiter-Info */}
                        <div className="relative text-center">
                          <div className="text-lg mb-1 drop-shadow-sm">
                            {companion.icon.startsWith('data:') ? (
                              <img 
                                src={companion.icon} 
                                alt={companion.name}
                                className="w-5 h-5 object-contain"
                                style={{ display: 'inline-block' }}
                              />
                            ) : (
                              companion.icon
                            )}
                          </div>
                          <h4 className="text-[10px] font-bold text-gray-800 leading-tight mb-1 break-words">{companion.name}</h4>
                          
                          {/* Level und Rarity in einer Zeile */}
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <div className="inline-flex items-center gap-0.5 bg-gray-600 text-white px-1 py-0.5 rounded text-[8px] font-medium">
                              <span>⚔️</span>
                              <span>Lv.{battleComp.battleLevel}</span>
                            </div>
                            <div className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium text-white ${
                              companion.type === 'legendary' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                              companion.type === 'epic' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                              companion.type === 'rare' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                              'bg-gradient-to-r from-gray-500 to-gray-600'
                            }`}>
                              <span>{companion.type === 'legendary' ? 'L' : 
                                    companion.type === 'epic' ? 'E' : 
                                    companion.type === 'rare' ? 'S' : 'G'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Kompakte Stats in einer Zeile */}
                        <div className="flex justify-between items-center bg-gray-100 rounded px-1 py-0.5 mb-1 border border-gray-200/50">
                          <div className="flex items-center gap-0.5 text-[8px]">
                            <span className="text-red-600">⚔️</span>
                            <span className="font-bold text-red-700">{formatNumber(battleComp.attack)}</span>
                          </div>
                          <div className="flex items-center gap-0.5 text-[8px]">
                            <span className="text-blue-600">🛡️</span>
                            <span className="font-bold text-blue-700">{formatNumber(battleComp.defense)}</span>
                          </div>
                          <div className="flex items-center gap-0.5 text-[8px]">
                            <span className="text-green-600">❤️</span>
                            <span className="font-bold text-green-700">{formatNumber(battleComp.maxHp)}</span>
                          </div>
                        </div>

                        {/* Kompakter Auswahl-Button */}
                        <div className={`
                          w-full py-0.5 px-1 rounded font-bold text-[8px] transition-all duration-300 text-center
                          ${isSelected 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm' 
                            : isTraining
                              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                              : canSelect 
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-sm' 
                                : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          }
                        `}>
                          {isSelected ? '✓' : isTraining ? '🏰' : canSelect ? 'Auswählen' : 'Max'}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>






        </div>
      )}

      {/* Kampf-Interface */}
      {activeTab === 'arena' && currentBattle && !battleResult && (
        <div className="relative z-10 mx-4 mt-4">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-r from-red-600/30 to-orange-600/30 rounded-2xl p-4 border border-red-400/50"
          >
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-white mb-2">KAMPF IM GANGE</h3>
              <div className="text-orange-300">Runde {(currentBattle as any)?.turn || 0}</div>
            </div>
            
            {/* Team-Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Spieler-Team */}
              <div className="bg-green-500/20 rounded-xl p-3 border border-green-400/30">
                <h4 className="text-green-400 font-bold mb-2">👥 Dein Team</h4>
                <div className="space-y-1">
                  {(currentBattle as any)?.playerTeam.map((companion: BattleCompanion, index: number) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span>{companion.icon}</span>
                        <span className="text-white">{companion.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className={`px-2 py-1 rounded text-[10px] ${companion.currentHp > 0 ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                          {formatNumber(companion.currentHp)}/{formatNumber(companion.maxHp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gegner-Team */}
              <div className="bg-red-500/20 rounded-xl p-3 border border-red-400/30">
                <h4 className="text-red-400 font-bold mb-2">🐺 Gegner</h4>
                <div className="space-y-1">
                  {(currentBattle as any)?.enemyTeam.map((enemy: BattleCompanion, index: number) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span>{enemy.icon}</span>
                        <span className="text-white">{enemy.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className={`px-2 py-1 rounded text-[10px] ${enemy.currentHp > 0 ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                          {formatNumber(enemy.currentHp)}/{formatNumber(enemy.maxHp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Toggle zwischen Kampf-Teilnehmern und Kampf-Verlauf - Desktop */}
            <div className="mt-4">
              {/* Toggle Buttons */}
              <div className="flex bg-black/20 rounded-xl p-1 border border-gray-500/30 mb-4 max-w-xs">
                <motion.button
                  onClick={() => setBattleView('participants')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
                    battleView === 'participants'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  👥 Teilnehmer
                </motion.button>
                <motion.button
                  onClick={() => setBattleView('log')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all ${
                    battleView === 'log'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  📜 Verlauf
                </motion.button>
              </div>

              {/* Kampf-Teilnehmer Anzeige - Desktop - Volle Modal-Breite */}
              {battleView === 'participants' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-black/40 rounded-2xl border-2 border-gray-400/50 p-6 shadow-2xl w-full"
                >
                  <div className="text-white font-bold mb-4 flex items-center gap-2 text-lg">
                    <span className="text-xl">⚔️</span> Kampf-Teilnehmer
                  </div>
                  
                  {/* Spieler-Team - Vergrößert */}
                  <div className="mb-6">
                    <div className="text-blue-300 text-base font-bold mb-3 flex items-center gap-1">👥 Dein Team:</div>
                    <div className="grid grid-cols-5 gap-4">
                      {(currentBattle as any)?.playerTeam?.map((companion: BattleCompanion, idx: number) => (
                        <div key={idx} className={`relative rounded-xl p-4 border-2 transition-all duration-300 hover:scale-105 ${
                          companion.currentHp <= 0 
                            ? 'bg-gradient-to-b from-gray-700/40 to-gray-800/40 border-gray-500/50 opacity-50' 
                            : companion.currentHp / companion.maxHp > 0.5 
                              ? 'bg-gradient-to-b from-blue-500/40 to-blue-600/40 border-blue-400/60 shadow-lg shadow-blue-500/20' 
                              : companion.currentHp / companion.maxHp > 0.25 
                                ? 'bg-gradient-to-b from-yellow-500/40 to-orange-500/40 border-yellow-400/60 shadow-lg shadow-yellow-500/20' 
                                : 'bg-gradient-to-b from-red-500/40 to-red-600/40 border-red-400/60 shadow-lg shadow-red-500/20'
                        }`}>
                          {/* Status-Indikator */}
                          <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
                            companion.currentHp <= 0 ? 'bg-gray-400' : 'bg-green-400 animate-pulse'
                          }`}></div>
                          
                          <div className="text-center mb-3">
                            <div className="text-2xl mb-2 drop-shadow-lg">
                            {companion.icon.startsWith('data:') ? (
                              <img 
                                src={companion.icon} 
                                alt={companion.name}
                                className="w-8 h-8 object-contain"
                                style={{ display: 'inline-block' }}
                              />
                            ) : (
                              companion.icon
                            )}
                          </div>
                            <div className="text-sm text-white truncate font-bold drop-shadow-sm">{companion.name}</div>
                          </div>
                          <div className="space-y-3">
                            {/* Lebensbalken - Verbessert */}
                            <div className="w-full bg-black/50 rounded-full h-2.5 overflow-hidden border border-white/20">
                              <motion.div 
                                className={`h-2.5 rounded-full bg-gradient-to-r ${
                                  companion.currentHp / companion.maxHp > 0.5 ? 'from-green-400 to-emerald-400' :
                                  companion.currentHp / companion.maxHp > 0.25 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-pink-400'
                                }`}
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.max(0, (companion.currentHp / companion.maxHp) * 100)}%` }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                              />
                            </div>
                            <div className="text-xs text-white text-center font-bold drop-shadow-sm">
                              {formatNumber(Math.max(0, Math.round(companion.currentHp)))}/{formatNumber(companion.maxHp)}
                            </div>
                          </div>
                        </div>
                      )) || []}
                    </div>
                  </div>

                  {/* Gegner-Team - Vergrößert */}
                  <div>
                    <div className="text-red-300 text-base font-bold mb-3 flex items-center gap-1">🐺 Gegner:</div>
                    <div className="grid grid-cols-5 gap-4">
                      {(currentBattle as any)?.enemyTeam?.map((enemy: BattleCompanion, idx: number) => (
                        <div key={idx} className={`relative rounded-xl p-4 border-2 transition-all duration-300 hover:scale-105 ${
                          enemy.currentHp <= 0 
                            ? 'bg-gradient-to-b from-gray-700/40 to-gray-800/40 border-gray-500/50 opacity-50' 
                            : enemy.currentHp / enemy.maxHp > 0.5 
                              ? 'bg-gradient-to-b from-red-500/40 to-red-600/40 border-red-400/60 shadow-lg shadow-red-500/20' 
                              : enemy.currentHp / enemy.maxHp > 0.25 
                                ? 'bg-gradient-to-b from-orange-500/40 to-red-500/40 border-orange-400/60 shadow-lg shadow-orange-500/20' 
                                : 'bg-gradient-to-b from-red-600/40 to-red-700/40 border-red-500/60 shadow-lg shadow-red-600/20'
                        }`}>
                          {/* Status-Indikator */}
                          <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
                            enemy.currentHp <= 0 ? 'bg-gray-400' : 'bg-red-400 animate-pulse'
                          }`}></div>
                          
                          <div className="text-center mb-3">
                            <div className="text-2xl mb-2 drop-shadow-lg">{enemy.icon}</div>
                            <div className="text-sm text-white truncate font-bold drop-shadow-sm">{enemy.name}</div>
                          </div>
                          <div className="space-y-3">
                            {/* Lebensbalken - Verbessert */}
                            <div className="w-full bg-black/50 rounded-full h-2.5 overflow-hidden border border-white/20">
                              <motion.div 
                                className={`h-2.5 rounded-full bg-gradient-to-r ${
                                  enemy.currentHp / enemy.maxHp > 0.5 ? 'from-green-400 to-emerald-400' :
                                  enemy.currentHp / enemy.maxHp > 0.25 ? 'from-yellow-400 to-orange-400' : 'from-red-400 to-pink-400'
                                }`}
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.max(0, (enemy.currentHp / enemy.maxHp) * 100)}%` }}
                                transition={{ duration: 0.5, ease: "easeOut" }}
                              />
                            </div>
                            <div className="text-xs text-white text-center font-bold drop-shadow-sm">
                              {formatNumber(Math.max(0, Math.round(enemy.currentHp)))}/{formatNumber(enemy.maxHp)}
                            </div>
                          </div>
                        </div>
                      )) || []}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Kampf-Verlauf Anzeige - Desktop */}
              {battleView === 'log' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-black/30 rounded-2xl border border-gray-500/30 max-h-48 overflow-y-auto p-4"
                >
                  <div className="text-white font-bold mb-2 flex items-center gap-2">
                    <span>📜</span> Kampf-Verlauf
                  </div>
                  <div className="space-y-1 text-sm">
                    {battleLog.length === 0 && (
                      <div className="text-gray-400 italic">Der Kampf beginnt ...</div>
                    )}
                    {battleLog.slice(-10).map((entry, idx) => (
                      <div key={idx} className="text-gray-300 py-1 border-b border-gray-600/30 last:border-0">
                        {entry}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Kampf-Animation */}
            <div className="mt-4 text-center">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="text-6xl"
              >
                
              </motion.div>
              <div className="text-white font-bold mt-2">Kampf läuft...</div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Kampf-Ergebnis */}
      {activeTab === 'arena' && battleResult && (
        <div className="relative z-10 mx-4 mt-4">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`rounded-2xl p-6 border-2 ${
              battleResult.winner === 'player' 
                ? 'bg-gradient-to-r from-green-600/30 to-emerald-600/30 border-green-400' 
                : 'bg-gradient-to-r from-red-600/30 to-pink-600/30 border-red-400'
            }`}
          >
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring" }}
                className="text-6xl mb-4"
              >
                {battleResult.winner === 'player' ? '🏆' : '💀'}
              </motion.div>
              <h3 className={`text-3xl font-bold mb-2 ${
                battleResult.winner === 'player' ? 'text-green-400' : 'text-red-400'
              }`}>
                {battleResult.winner === 'player' ? 'SIEG!' : 'NIEDERLAGE!'}
              </h3>
              <div className="text-gray-300">
                {battleResult.winner === 'player' 
                  ? 'Dein Team hat triumphiert!' 
                  : 'Das war ein harter Kampf...'}
              </div>
            </div>

            {/* Belohnungen */}
            {battleResult.winner === 'player' && (
              <motion.div 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-yellow-500/20 rounded-xl p-4 border border-yellow-400/30 mb-4"
              >
                <h4 className="text-yellow-400 font-bold text-lg mb-3 text-center">🎁 Belohnungen</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-yellow-500/20 rounded-xl p-3 text-center border border-yellow-400/30">
                    <div className="text-2xl mb-1">💰</div>
                    <div className="text-yellow-300 text-xs font-medium">Münzen</div>
                    <div className="text-white font-bold text-lg">+{formatNumber(battleResult.rewards.coins)}</div>
                  </div>
                  <div className="bg-blue-500/20 rounded-xl p-3 text-center border border-blue-400/30">
                    <div className="text-2xl mb-1">💎</div>
                    <div className="text-blue-300 text-xs font-medium">Diamanten</div>
                    <div className="text-white font-bold text-lg">+{formatNumber(battleResult.rewards.gems)}</div>
                  </div>
                  <div className="bg-purple-500/20 rounded-xl p-3 text-center border border-purple-400/30">
                    <div className="text-2xl mb-1">⭐</div>
                    <div className="text-purple-300 text-xs font-medium">Erfahrung</div>
                    <div className="text-white font-bold text-lg">+{formatNumber(battleResult.rewards.experience)}</div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Gefangener Begleiter */}
            {battleResult.winner === 'player' && battleResult.capturedCompanion && (
              <motion.div 
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-green-500/20 rounded-xl p-4 border border-green-400/30 mb-4"
              >
                <h4 className="text-green-400 font-bold text-lg mb-3 text-center">🎣 Gefangener Begleiter!</h4>
                <div className="bg-green-500/10 rounded-xl p-3 border border-green-400/30">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{battleResult.capturedCompanion.icon}</div>
                    <div className="flex-1">
                      <div className="text-white font-bold text-lg">{battleResult.capturedCompanion.name}</div>
                      <div className="text-green-300 text-sm">{battleResult.capturedCompanion.description}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          battleResult.capturedCompanion.type === 'legendary' ? 'bg-purple-600 text-white' :
                          battleResult.capturedCompanion.type === 'epic' ? 'bg-pink-600 text-white' :
                          battleResult.capturedCompanion.type === 'rare' ? 'bg-blue-600 text-white' :
                          'bg-gray-600 text-white'
                        }`}>
                          {battleResult.capturedCompanion.type.toUpperCase()}
                        </span>
                        <span className="text-green-300 text-xs">Level 1</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 text-green-300 text-sm">
                    ✨ Dieser Begleiter wurde zu deiner Sammlung hinzugefügt!
                  </div>
                </div>
              </motion.div>
            )}

            {/* Kampf-Log */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-black/30 rounded-2xl border border-gray-500/30 mb-4"
            >
              <details className="group">
                <summary className="p-4 cursor-pointer flex items-center justify-between text-white font-medium">
                  <span>📜 Kampf-Verlauf</span>
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-4 pb-4 max-h-40 overflow-y-auto">
                  <div className="space-y-1 text-sm">
                    {battleLog.map((entry, idx) => (
                      <div key={idx} className="text-gray-300 py-1 border-b border-gray-600/30 last:border-0">
                        {entry}
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex gap-2"
            >
              <motion.button
                onClick={() => endBattle(true)}
                className="flex-1 bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 px-4 rounded-2xl font-bold text-lg border border-gray-500 shadow-lg"
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.02 }}
              >
                🏠 Zurück
              </motion.button>
              
              <motion.button
                onClick={() => {
                  const currentTeam = selectedTeam;
                  endBattle(false); // Auto-Kampf nicht stoppen beim Erneut-Button
                  setTimeout(() => {
                    setSelectedTeam(currentTeam);
                    startBattle();
                  }, 100);
                }}
                className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 px-4 rounded-2xl font-bold text-lg border border-orange-400 shadow-lg shadow-orange-500/25"
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.02 }}
              >
                🔄 Erneut
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      )}

      {/* Kaserne Tab */}
      {activeTab === 'barracks' && (
        <div className="relative z-10 mx-4 mt-4 space-y-4">
          {/* Training Status */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-4 border border-blue-400/30"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-bold text-lg flex items-center gap-2">
                <span>🏰</span>Kaserne
              </h4>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <div className="text-blue-300 text-sm">Aktiv: {trainingCompanions.length}/{getMaxTrainingCapacity()}</div>

                </div>
                {trainingCompanions.length > 0 && (
                  <button
                    onClick={() => trainingCompanions.forEach(id => removeFromTraining(id))}
                    className="text-red-400 text-sm px-2 py-1 rounded bg-red-500/20 border border-red-400/30"
                  >
                    Alle entfernen
                  </button>
                )}
              </div>
            </div>
            
            {/* Training Kosten - Optimiert */}
            <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-400/30 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-blue-400 font-bold">💰 Training Kosten</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`text-lg font-bold ${canAffordTraining() ? 'text-green-400' : 'text-red-400'}`}>
                    {formatNumber(getTrainingCost())}/s
                  </div>
                  {!canAffordTraining() && trainingCompanions.length > 0 && (
                    <div className="text-red-400 text-xs">⚠️ Zu wenig Münzen</div>
                  )}
                </div>
              </div>
              
              {/* Detaillierte Kosten-Breakdown */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-blue-500/20 rounded p-2 border border-blue-400/30">
                  <div className="text-blue-300 font-medium mb-1">📊 Aktuelle Kosten</div>
                  <div className="text-white">
                    <div>Pro Sekunde: {formatNumber(getTrainingCost())} Münzen</div>
                    <div>Pro Minute: {formatNumber(getTrainingCost() * 60)} Münzen</div>
                    <div>Pro Stunde: {formatNumber(getTrainingCost() * 3600)} Münzen</div>
                  </div>
                </div>
                
                <div className="bg-green-500/20 rounded p-2 border border-green-400/30">
                  <div className="text-green-300 font-medium mb-1">💸 Verfügbare Münzen</div>
                  <div className="text-white">
                    <div>Münzen: {formatNumber(coins)}</div>
                    <div>Verfügbar: {canAffordTraining() ? '✅ Genug' : '❌ Zu wenig'}</div>
                    <div>Zeit bis leer: {formatTimeUntilEmpty()}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Aktive Trainings-Begleiter */}
            {trainingCompanions.length > 0 && (
              <div className="space-y-2">
                <div className="text-white font-medium mb-2">🎯 In Training:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {trainingCompanions.map(companionId => {
                    const companion = getTrainingCompanion(companionId);
                    if (!companion) return null;
                    
                    const battleComp = toBattleCompanion(companion);
                    const currentExp = companion.battleExperience || 0;
                    const requiredExp = (companion.battleLevel || 1) * 100;
                    const expProgress = (currentExp / requiredExp) * 100;
                    
                    return (
                      <motion.div
                        key={companionId}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        onClick={() => removeFromTraining(companionId)}
                        className="relative group cursor-pointer"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {/* Kompakte Begleiter-Karte - Training */}
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-md border border-white/50 hover:shadow-lg transition-all duration-300 overflow-hidden bg-blue-500/10">
                          {/* Training Glow-Effekt */}
                          <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-400/20 via-purple-400/20 to-blue-400/20"></div>
                          
                          {/* Training Badge */}
                          <motion.div 
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-0.5 -right-0.5 bg-blue-500 rounded-full w-3 h-3 flex items-center justify-center text-white font-bold border border-white shadow-sm text-[8px] z-10"
                          >
                            🏰
                          </motion.div>
                          
                          {/* Kompakte Begleiter-Info */}
                          <div className="relative text-center">
                            <div className="text-lg mb-1 drop-shadow-sm">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-5 h-5 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </div>
                            <h4 className="text-[10px] font-bold text-gray-800 leading-tight mb-1 break-words">{companion.name}</h4>
                            
                            {/* Level und Rarity in einer Zeile */}
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <div className="inline-flex items-center gap-0.5 bg-blue-600 text-white px-1 py-0.5 rounded text-[8px] font-medium">
                                <span>⚔️</span>
                                <span>Lv.{battleComp.battleLevel}</span>
                              </div>
                              <div className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium text-white ${
                                companion.type === 'legendary' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                                companion.type === 'epic' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                                companion.type === 'rare' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                                'bg-gradient-to-r from-gray-500 to-gray-600'
                              }`}>
                                <span>{companion.type === 'legendary' ? 'L' : 
                                      companion.type === 'epic' ? 'E' : 
                                      companion.type === 'rare' ? 'S' : 'G'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Kompakte XP Progress */}
                          <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 rounded px-1 py-0.5 mb-1 border border-blue-200/50">
                            <div className="flex justify-between text-[8px] text-gray-600 mb-0.5">
                              <span>⭐ {formatNumber(currentExp)}</span>
                              <span>/{formatNumber(requiredExp)}</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                              <motion.div 
                                className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                                style={{ width: `${Math.min(100, expProgress)}%` }}
                                animate={{ width: `${Math.min(100, expProgress)}%` }}
                                transition={{ duration: 0.5 }}
                              />
                            </div>
                            
                            {/* Kompakte Zeit-Anzeige */}
                            <div className="text-center mt-0.5">
                              {(() => {
                                const xpNeeded = requiredExp - currentExp;
                                const xpPerSecond = 5; // Angepasst: 5 XP pro Sekunde (10x langsamer)
                                const secondsNeeded = Math.ceil(xpNeeded / xpPerSecond);
                                
                                if (secondsNeeded <= 0) {
                                  return <span className="text-green-600 text-[8px] font-bold">✨ Level-Up!</span>;
                                }
                                
                                const minutes = Math.floor(secondsNeeded / 60);
                                const seconds = secondsNeeded % 60;
                                
                                if (minutes > 0) {
                                  return (
                                    <span className="text-blue-600 text-[8px]">
                                      ⏱️ {minutes}m {seconds}s
                                    </span>
                                  );
                                } else {
                                  return (
                                    <span className="text-yellow-600 text-[8px]">
                                      ⏱️ {seconds}s
                                    </span>
                                  );
                                }
                              })()}
                            </div>
                          </div>

                          {/* Kompakte Stats in einer Zeile */}
                          <div className="flex justify-between items-center bg-gray-100 rounded px-1 py-0.5 mb-1 border border-gray-200/50">
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-red-600">⚔️</span>
                              <span className="font-bold text-red-700">{formatNumber(battleComp.attack)}</span>
                            </div>
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-blue-600">🛡️</span>
                              <span className="font-bold text-blue-700">{formatNumber(battleComp.defense)}</span>
                            </div>
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-green-600">❤️</span>
                              <span className="font-bold text-green-700">{formatNumber(battleComp.maxHp)}</span>
                            </div>
                          </div>

                          {/* Kompakter Training-Button */}
                          <div className="w-full py-0.5 px-1 rounded font-bold text-[8px] transition-all duration-300 text-center bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm">
                            🏰 Training
                          </div>
                          
                          {/* Kompakter Entfernen-Hinweis */}
                          <div className="text-center mt-0.5">
                            <div className="text-red-500 text-[8px] font-medium">
                              🗑️ Klicken zum Entfernen
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>

          {/* Verfügbare Begleiter für Training */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl p-3 border border-purple-400/30"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-bold text-lg flex items-center gap-2">
                <span>👥</span>Begleiter auswählen
              </h4>
              <button
                onClick={addBestCompanionsToTraining}
                disabled={trainingCompanions.length >= getMaxTrainingCapacity() || companions.discovered.filter(c => !trainingCompanions.includes(c.id)).length === 0}
                className="text-blue-400 text-sm px-2 py-1 rounded bg-blue-500/20 border border-blue-400/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ⭐ Beste hinzufügen
              </button>
            </div>
            
            <div className="max-h-80 overflow-y-auto p-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {companions.discovered
                  .filter(companion => !trainingCompanions.includes(companion.id))
                  .map((companion, index) => {
                    const battleComp = toBattleCompanion(companion);
                    const canAdd = trainingCompanions.length < getMaxTrainingCapacity();
                    
                    return (
                      <motion.div
                        key={companion.id}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.02 * index }}
                        onClick={() => canAdd && addToTraining(companion.id)}
                        className="relative group cursor-pointer"
                        whileHover={canAdd ? { scale: 1.05, y: -2 } : {}}
                        whileTap={canAdd ? { scale: 0.95 } : {}}
                      >
                        {/* Kompakte Begleiter-Karte - Kaserne */}
                        <div className={`
                          relative bg-white/90 backdrop-blur-sm rounded-lg p-1.5 shadow-md border border-white/50 hover:shadow-lg transition-all duration-300 overflow-hidden
                          ${canAdd 
                            ? 'hover:bg-white/95' 
                            : 'opacity-50 cursor-not-allowed bg-gray-500/50'
                          }
                        `}>
                          {/* Rarity Glow-Effekt */}
                          <div className={`absolute inset-0 rounded-lg ${
                            companion.type === 'legendary' ? 'bg-gradient-to-br from-yellow-400/20 via-orange-400/20 to-red-400/20' :
                            companion.type === 'epic' ? 'bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-purple-400/20' :
                            companion.type === 'rare' ? 'bg-gradient-to-br from-blue-400/20 via-cyan-400/20 to-blue-400/20' :
                            'bg-gradient-to-br from-gray-400/20 via-gray-300/20 to-gray-400/20'
                          }`}></div>
                          
                          {/* Kompakte Begleiter-Info */}
                          <div className="relative text-center">
                            <div className="text-lg mb-1 drop-shadow-sm">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-5 h-5 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </div>
                            <h4 className="text-[10px] font-bold text-gray-800 leading-tight mb-1 break-words">{companion.name}</h4>
                            
                            {/* Level und Rarity in einer Zeile */}
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <div className="inline-flex items-center gap-0.5 bg-gray-600 text-white px-1 py-0.5 rounded text-[8px] font-medium">
                                <span>⚔️</span>
                                <span>Lv.{battleComp.battleLevel}</span>
                              </div>
                              <div className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium text-white ${
                                companion.type === 'legendary' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                                companion.type === 'epic' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
                                companion.type === 'rare' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                                'bg-gradient-to-r from-gray-500 to-gray-600'
                              }`}>
                                <span>{companion.type === 'legendary' ? 'L' : 
                                      companion.type === 'epic' ? 'E' : 
                                      companion.type === 'rare' ? 'S' : 'G'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Kompakte Stats in einer Zeile */}
                          <div className="flex justify-between items-center bg-gray-100 rounded px-1 py-0.5 mb-1 border border-gray-200/50">
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-red-600">⚔️</span>
                              <span className="font-bold text-red-700">{formatNumber(battleComp.attack)}</span>
                            </div>
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-blue-600">🛡️</span>
                              <span className="font-bold text-blue-700">{formatNumber(battleComp.defense)}</span>
                            </div>
                            <div className="flex items-center gap-0.5 text-[8px]">
                              <span className="text-green-600">❤️</span>
                              <span className="font-bold text-green-700">{formatNumber(battleComp.maxHp)}</span>
                            </div>
                          </div>

                          {/* Kompakter Auswahl-Button */}
                          <div className={`
                            w-full py-0.5 px-1 rounded font-bold text-[8px] transition-all duration-300 text-center
                            ${canAdd 
                              ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 shadow-sm' 
                              : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            }
                          `}>
                            {canAdd ? `🏰 Hinzufügen` : `Max (${getMaxTrainingCapacity()})`}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Floating Arena-Reset Button - erscheint ab Arena Level 50 und mindestens 1 Begleiter ausgewählt */}
      <AnimatePresence>
        {canResetArena() && arenaLevel >= 50 && selectedTeam.length > 0 && activeTab === 'arena' && !currentBattle && !battleResult && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              duration: 0.3 
            }}
            className="fixed bottom-20 left-4 right-4 z-50 sm:hidden"
          >
            <motion.button
              onClick={() => resetArena(selectedTeam.map(c => c.id))}
              className="w-full py-3 px-6 rounded-xl font-bold text-base shadow-2xl border-2 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 text-white border-white/20 backdrop-blur-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(147, 51, 234, 0.3)"
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🔄
                </motion.div>
                <span>Arena Reset (Lv.{arenaLevel})</span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="text-lg"
                >
                  🔄
                </motion.div>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Kampf Button - erscheint nur bei 5 ausgewählten Begleitern */}
      <AnimatePresence>
        {selectedTeam.length === 5 && activeTab === 'arena' && !currentBattle && !battleResult && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              duration: 0.3 
            }}
            className="fixed bottom-4 left-4 right-4 z-50 sm:hidden"
          >
            <motion.button
              onClick={() => startBattle()}
              className="w-full py-4 px-6 rounded-xl font-bold text-lg shadow-2xl border-2 bg-gradient-to-r from-red-600 via-orange-600 to-red-700 text-white border-white/20 backdrop-blur-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(239, 68, 68, 0.3)"
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-xl"
                >
                  ⚔️
                </motion.div>
                <span>Kampf starten!</span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="text-xl"
                >
                  ⚔️
                </motion.div>
              </div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Arena-Reset Button - nur auf größeren Bildschirmen sichtbar ab Level 50 und mindestens 1 Begleiter ausgewählt */}
      {canResetArena() && arenaLevel >= 50 && selectedTeam.length > 0 && activeTab === 'arena' && !currentBattle && !battleResult && (
        <div className="hidden sm:block mt-4 mx-4 mb-4">
          <motion.button
            onClick={() => resetArena(selectedTeam.map(c => c.id))}
            className="w-full py-3 px-6 rounded-xl font-bold text-base bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 text-white shadow-lg hover:shadow-xl transition-all border-2 border-purple-400/30"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            style={{
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(147, 51, 234, 0.2)"
            }}
          >
            <div className="flex items-center justify-center gap-2">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="text-lg"
              >
                🔄
              </motion.div>
              <span>Arena Reset (Level {arenaLevel}) - Schwierigkeit: +{Math.round((getArenaDifficultyMultiplier() - 1) * 100)}%</span>
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="text-lg"
              >
                🔄
              </motion.div>
            </div>
          </motion.button>
        </div>
      )}

      {/* Desktop Kampf Button - nur auf größeren Bildschirmen sichtbar */}
      {selectedTeam.length === 5 && activeTab === 'arena' && !currentBattle && !battleResult && (
        <div className="hidden sm:block mt-4 mx-4">
          <motion.button
            onClick={() => startBattle()}
            className="w-full py-4 px-6 rounded-xl font-bold text-lg bg-gradient-to-r from-red-600 via-orange-600 to-red-700 text-white shadow-lg hover:shadow-xl transition-all border-2 border-red-400/30"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            style={{
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.3), 0 0 10px rgba(239, 68, 68, 0.2)"
            }}
          >
            <div className="flex items-center justify-center gap-3">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-xl"
              >
                ⚔️
              </motion.div>
              <span>Kampf starten!</span>
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-xl"
              >
                ⚔️
              </motion.div>
            </div>
          </motion.button>
        </div>
      )}

      {/* Desktop Kampf Button - deaktiviert wenn nicht 5 Begleiter ausgewählt */}
      {selectedTeam.length < 5 && activeTab === 'arena' && !currentBattle && !battleResult && (
        <div className="hidden sm:block mt-4 mx-4">
          <motion.button
            disabled={true}
            className="w-full py-4 px-6 rounded-xl font-bold text-lg bg-gray-700/50 text-gray-400 cursor-not-allowed border-2 border-gray-500/30"
            whileHover={{ scale: 1.0 }}
            whileTap={{ scale: 1.0 }}
          >
            <div className="flex items-center justify-center gap-3">
              <span>⚔️ Wähle {5 - selectedTeam.length} weitere Begleiter</span>
            </div>
          </motion.button>
        </div>
      )}

      {/* Kampf-Analyse Mini-Modal */}
      <AnimatePresence>
        {showBattleAnalysis && selectedTeam.length === 5 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowBattleAnalysis(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-gradient-to-b from-red-900 via-orange-900 to-red-800 rounded-xl border border-red-400/50 shadow-2xl w-auto min-w-[280px] max-w-[90vw] mx-4 h-fit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Kompakter Header */}
              <div className="p-3 border-b border-red-400/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🎯</span>
                    <h3 className="text-white font-bold">Kampf-Analyse</h3>
                  </div>
                  <button
                    onClick={() => setShowBattleAnalysis(false)}
                    className="text-gray-400 hover:text-white text-lg"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Kompakter Content */}
              <div className="p-3 space-y-3">
                {/* Schneller Team-Vergleich */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-green-500/10 rounded-lg p-2 border border-green-400/30">
                    <div className="text-green-400 font-bold text-sm mb-1">👥 Dein Team</div>
                    <div className="text-xs text-gray-300 space-y-0.5">
                      <div>Stärke: {formatNumber(Math.round(calculateTeamPower(selectedTeam.map(c => toBattleCompanion(c)))))}</div>
                      <div>Synergien: {new Set(selectedTeam.flatMap(c => c.abilities.map(a => a.type))).size}</div>
                    </div>
                  </div>

                  <div className="bg-red-500/10 rounded-lg p-2 border border-red-400/30">
                    <div className="text-red-400 font-bold text-sm mb-1">🐺 Gegner</div>
                    <div className="text-xs text-gray-300 space-y-0.5">
                      <div>Stärke: {formatNumber(Math.round(calculateTeamPower(previewEnemyTeam)))}</div>
                      <div>Level: {arenaLevel}</div>
                    </div>
                  </div>
                </div>

                {/* Kompakte Gewinnchancen */}
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-2 border border-blue-400/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-blue-400 font-bold text-sm">🎲 Gewinnchance</span>
                    <div className={`text-lg font-bold ${
                      calculateWinChance() >= 70 ? 'text-green-400' :
                      calculateWinChance() >= 40 ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {Math.round(calculateWinChance())}%
                    </div>
                  </div>
                  
                  <div className="bg-gray-700 rounded-full h-2 overflow-hidden">
                    <motion.div 
                      className={`h-full rounded-full ${
                        calculateWinChance() >= 70 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                        calculateWinChance() >= 40 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                        'bg-gradient-to-r from-red-500 to-pink-500'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${calculateWinChance()}%` }}
                      transition={{ duration: 1, delay: 0.2 }}
                    />
                  </div>
                </div>

                {/* Kompakte Team-Details */}
                <div className="bg-gray-800/50 rounded-lg p-2 border border-gray-600/30">
                  <div className="text-white font-bold text-sm mb-2">👥 Team Details:</div>
                  <div className="space-y-1">
                    {selectedTeam.map((companion, index) => {
                      const battleComp = toBattleCompanion(companion);
                      return (
                        <div key={companion.id} className="flex items-center justify-between bg-gray-700/30 rounded p-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm">
                              {companion.icon.startsWith('data:') ? (
                                <img 
                                  src={companion.icon} 
                                  alt={companion.name}
                                  className="w-4 h-4 object-contain"
                                  style={{ display: 'inline-block' }}
                                />
                              ) : (
                                companion.icon
                              )}
                            </span>
                            <div>
                              <div className="text-white text-xs font-medium">{companion.name}</div>
                              <div className="text-gray-400 text-[10px]">Lv.{battleComp.battleLevel}</div>
                            </div>
                          </div>
                          <div className="text-right text-[10px] text-gray-300">
                            <div>⚔️{formatNumber(battleComp.attack)} 🛡️{formatNumber(battleComp.defense)} ❤️{formatNumber(battleComp.maxHp)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Kompakte Empfehlung */}
                <div className="bg-yellow-500/10 rounded-lg p-2 border border-yellow-400/30">
                  <div className="text-yellow-400 font-bold text-sm mb-1">💡 Empfehlung</div>
                  <div className="text-xs text-gray-300">
                    {calculateWinChance() >= 70 ? (
                      <div>✅ Dein Team ist sehr stark!</div>
                    ) : calculateWinChance() >= 40 ? (
                      <div>⚠️ Das wird ein knapper Kampf.</div>
                    ) : (
                      <div>❌ Trainiere deine Begleiter weiter.</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Kompakter Footer */}
              <div className="p-3 border-t border-red-400/30">
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowBattleAnalysis(false)}
                    className="flex-1 py-2 px-3 bg-gray-600 text-white rounded-lg font-medium text-sm"
                  >
                    Schließen
                  </button>
                  <button
                    onClick={() => {
                      setShowBattleAnalysis(false);
                      startBattle();
                    }}
                    className="flex-1 py-2 px-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg font-medium text-sm"
                  >
                    ⚔️ Kampf starten
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Victory Modal */}
        <AnimatePresence>
          {showVictoryModal && battleResult && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl p-6 max-w-sm w-full border border-green-400 shadow-2xl"
              >
                <div className="text-center">
                  <motion.div 
                    className="text-6xl mb-4"
                    animate={{ 
                      scale: [1, 1.2, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    🏆
                  </motion.div>
                  
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Sieg!
                  </h2>
                  
                  <p className="text-white/90 text-lg mb-4">
                    Dein Team hat triumphiert!
                  </p>
                  
                  {/* Belohnungen */}
                  <motion.div 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white/10 rounded-xl p-4 mb-6 border border-white/20"
                  >
                    <h3 className="text-white font-bold text-lg mb-3">🎁 Belohnungen</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <div className="text-2xl mb-1">💰</div>
                        <div className="text-white/80 text-xs">Münzen</div>
                        <div className="text-white font-bold">+{formatNumber(battleResult.rewards.coins)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl mb-1">💎</div>
                        <div className="text-white/80 text-xs">Edelsteine</div>
                        <div className="text-white font-bold">+{formatNumber(battleResult.rewards.gems)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl mb-1">⭐</div>
                        <div className="text-white/80 text-xs">Erfahrung</div>
                        <div className="text-white font-bold">+{formatNumber(battleResult.rewards.experience)}</div>
                      </div>
                    </div>
                  </motion.div>
                  
                  <motion.button
                    onClick={() => setShowVictoryModal(false)}
                    className="w-full bg-white text-green-700 py-3 px-6 rounded-xl font-bold text-lg shadow-lg"
                    whileTap={{ scale: 0.95 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    Weiter
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AnimatePresence>
    </div>
  );
};

export default CompanionArena; 